/**
 * Millimetres in, screen pixels out.
 *
 * The document is measured in millimetres and Konva draws in pixels, so exactly
 * one place in the app is allowed to convert between them. Anywhere else that
 * multiplies by a scale factor is a bug waiting to disagree with the ruler.
 */

import type { AssetRef, Colour, Ink } from './types';
import { inkToRgb } from '../colour/convert';

/** CSS pixels per millimetre at 100% zoom - the CSS reference of 96 dpi. */
export const PX_PER_MM = 96 / 25.4;

/** Points per millimetre. Type is specified in points; everything else is mm. */
export const PT_PER_MM = 72 / 25.4;

export const mmToPx = (mm: number, zoom = 1) => mm * PX_PER_MM * zoom;
export const pxToMm = (px: number, zoom = 1) => px / (PX_PER_MM * zoom);
export const ptToMm = (pt: number) => pt / PT_PER_MM;
export const mmToPt = (mm: number) => mm * PT_PER_MM;

/** Rounded the way the document stores it, so the view and the file agree. */
export const roundMm = (mm: number) => Math.round(mm * 1000) / 1000;

/**
 * A measurement as a customer should read it.
 *
 * Two decimals, because a third is below what any of our presses can hold and
 * showing it only invites someone to try to hit it.
 */
export function formatMm(mm: number): string {
  return `${(Math.round(mm * 100) / 100).toFixed(2).replace(/\.?0+$/, '')} mm`;
}

/* ---------------------------------------------------------------
 * The customer's own units
 *
 * The document is millimetres and stays millimetres - the press, the proof and
 * every saved design are. What changes is only how a length is shown and typed:
 * a customer who thinks in inches reads 3.5 in where the file says 88.9. So a
 * unit is a pair of conversions at the edge of the interface and nothing more.
 * ------------------------------------------------------------ */

export type Unit = 'mm' | 'cm' | 'in' | 'pt';

export interface UnitInfo {
  id: Unit;
  /** In a menu. */
  name: string;
  /** Beside a number. */
  short: string;
  /** How many of this unit make a millimetre. */
  perMm: number;
  /**
   * Decimals worth showing. Each is about a hundredth of a millimetre, which
   * is finer than any press holds and coarse enough that 3.5 in reads 3.5.
   */
  decimals: number;
}

export const UNITS: UnitInfo[] = [
  { id: 'mm', name: 'Millimetres', short: 'mm', perMm: 1, decimals: 2 },
  { id: 'cm', name: 'Centimetres', short: 'cm', perMm: 0.1, decimals: 3 },
  { id: 'in', name: 'Inches', short: 'in', perMm: 1 / 25.4, decimals: 3 },
  { id: 'pt', name: 'Points', short: 'pt', perMm: 72 / 25.4, decimals: 2 },
];

export function unitInfo(unit: Unit): UnitInfo {
  return UNITS.find((each) => each.id === unit) ?? UNITS[0]!;
}

export function isUnit(value: unknown): value is Unit {
  return UNITS.some((each) => each.id === value);
}

/** A length in millimetres, in the given unit, rounded for reading. */
export function fromMm(mm: number, unit: Unit): number {
  const info = unitInfo(unit);
  const scale = Math.pow(10, info.decimals);

  return Math.round(mm * info.perMm * scale) / scale;
}

/** A length typed in the given unit, as the millimetres the document stores. */
export function toMm(value: number, unit: Unit): number {
  return roundMm(value / unitInfo(unit).perMm);
}

/** "90 mm", "9 cm", "3.543 in" - no trailing zeros, the unit said once. */
export function formatLength(mm: number, unit: Unit): string {
  return `${trimZeros(fromMm(mm, unit))} ${unitInfo(unit).short}`;
}

/** "90 × 55 mm" - two lengths, the unit said once at the end. */
export function formatSize(w: number, h: number, unit: Unit): string {
  return `${trimZeros(fromMm(w, unit))} × ${trimZeros(fromMm(h, unit))} ${unitInfo(unit).short}`;
}

function trimZeros(value: number): string {
  return String(value);
}

/**
 * A step for a number field, in the given unit, from one in millimetres.
 *
 * Rounded to 1, 2 or 5 of a power of ten, so the up-arrow on a corner that
 * moves 0.5 mm moves 0.02 in rather than 0.019685.
 */
export function stepIn(stepMm: number, unit: Unit): number {
  const raw = stepMm * unitInfo(unit).perMm;

  if (raw <= 0) return 1;

  const power = Math.pow(10, Math.floor(Math.log10(raw)));
  const lead = raw / power;
  const nice = lead < 1.5 ? 1 : lead < 3.5 ? 2 : lead < 7.5 ? 5 : 10;

  return Math.round(nice * power * 1e6) / 1e6;
}

/* ---------------------------------------------------------------
 * Colour, for the screen only
 * ------------------------------------------------------------ */

/**
 * A CSS colour for any document colour.
 *
 * Ink is shown as it prints, through the same conversion Ghostscript uses for
 * the press PDF (colour/convert.ts), so the canvas, the SVG proof and the PDF
 * show the same colour.
 */
export function toCss(colour: Colour | null | undefined): string {
  if (!colour) return '#000000';

  if (colour.space === 'rgb') return colour.value;

  const ink: Ink = colour.space === 'spot' ? colour.fallback : colour.value;
  const [r, g, b] = inkToRgb(ink);

  return `rgb(${r}, ${g}, ${b})`;
}

/** Total ink, as a percentage. Null when the colour has no ink to total. */
export function coverage(colour: Colour): number | null {
  if (colour.space === 'rgb') return null;

  const ink = colour.space === 'spot' ? colour.fallback : colour.value;

  return Math.round(ink.reduce((sum, v) => sum + v, 0) * 1000) / 10;
}

/* ---------------------------------------------------------------
 * Resolution
 * ------------------------------------------------------------ */

/** Below this an image prints visibly soft; above it, nobody notices. */
export const DPI_GOOD = 200;

/** Below this it prints badly enough to be worth stopping to fix. */
export const DPI_POOR = 150;

/**
 * How many of an image's own pixels land in each printed inch at its current
 * size on the page. Placing an image larger lowers this; there is no way to
 * raise it but to supply a bigger file.
 */
export function effectiveDpi(asset: AssetRef | undefined, widthMm: number): number {
  if (!asset || !asset.w || widthMm <= 0) return 0;

  return Math.round(asset.w / (widthMm / 25.4));
}

export function dpiVerdict(dpi: number): 'good' | 'soft' | 'poor' {
  if (dpi >= DPI_GOOD) return 'good';

  return dpi >= DPI_POOR ? 'soft' : 'poor';
}
